import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ageVerificationAPI } from '../services/api';
import { useVerificationAvailability } from '../hooks/queries/useVerificationAvailability';
import { Button } from './ui/Button';
import { Spinner } from './Spinner';
import { Modal } from './ui/Modal';
import { unwrapResponse } from '../utils/unwrapResponse';
import { parseDateOnly, formatDateOnlyFromDate } from '../utils/dateUtils';

interface AgeVerificationModalProps {
  isOpen: boolean;
  onVerified: () => void;
}

export const AgeVerificationModal: React.FC<AgeVerificationModalProps> = ({ isOpen, onVerified }) => {
  const { verifyAge } = useAuth();
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDobForm, setShowDobForm] = useState(false);

  const { data: availability, isLoading: servicesLoading } = useVerificationAvailability(isOpen);
  const diditAvailable = servicesLoading ? null : (availability?.diditAvailable ?? false);

  const today = new Date();
  // These bounds are LOCAL-midnight Dates and are used two ways: compared against
  // parseDateOnly(dob) in validateAge, and formatted into the date input's max/min.
  // Format them with formatDateOnlyFromDate, never `.toISOString().split('T')[0]` —
  // toISOString converts to UTC, rolling the date part back a day in every zone AHEAD
  // of UTC. That set the picker's `max` one day early, so the BROWSER blocked a user
  // from selecting their real 18th birthday before validateAge ever ran (0zzz-bo).
  const minAge18Date = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  const maxAge120Date = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());

  const validateAge = (dobString: string): string | null => {
    if (!dobString) return 'Date of birth is required';
    // parseDateOnly, matching useRegister.ts's isValidAge (2026-08-15 Onboarding
    // red-team fix, HIGH) -- this modal is the twin that fix missed. A plain
    // `new Date("YYYY-MM-DD")` parses as UTC midnight while today/minAge18Date/
    // maxAge120Date above are local-time Dates, so the two comparison spaces
    // disagree on the 18th-birthday boundary. Measured: in zones AHEAD of UTC a
    // user whose 18th birthday is exactly today was told "You must be 18 or
    // older". It never admitted an under-18 -- the skew is strictly toward
    // false rejection -- but it locked out real adults on their birthday.
    const dob = parseDateOnly(dobString);
    if (isNaN(dob.getTime())) return 'Invalid date';
    if (dob > today) return 'Date of birth cannot be in the future';
    if (dob > minAge18Date) return 'You must be 18 or older to use this service';
    if (dob < maxAge120Date) return 'Please enter a valid date of birth';
    return null;
  };

  const handleBiometricChoice = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ageVerificationAPI.createDiditSession({
        successRedirect: window.location.href,
        errorRedirect: window.location.href,
      });
      if (unwrapResponse<any>(response.data).sessionUrl) window.location.href = unwrapResponse<any>(response.data).sessionUrl;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start biometric verification');
    } finally { setLoading(false); }
  };

  const handleDobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validationError = validateAge(dateOfBirth);
    if (validationError) { setError(validationError); return; }
    setLoading(true);
    try {
      await verifyAge(dateOfBirth);
      onVerified();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setError(message.includes('18') ? 'You must be 18 or older to use this service' : (message || 'Age verification failed'));
    } finally { setLoading(false); }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {/* Blocking modal — cannot be dismissed */}}
      title="Age Verification Required"
      size="md"
      variant="centered"
    >
      <p className="text-sm text-text-secondary m-0 mb-6">To continue using this service, please verify your age.</p>

      {servicesLoading ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <Spinner size="md" />
          <p className="text-sm text-text-secondary m-0">Checking verification options...</p>
        </div>
      ) : !showDobForm && diditAvailable ? (
        <div className="flex flex-col gap-3">
          <Button type="button" variant="ghost" onClick={handleBiometricChoice} disabled={loading}
            className="flex items-center gap-3.5 w-full p-4 h-auto rounded-xl text-left transition-all border border-accent bg-aegean/5 hover:bg-transparent active:bg-transparent hover:border-accent disabled:opacity-60 disabled:cursor-not-allowed">
            <div className="w-10 h-10 rounded-full bg-aegean/10 flex items-center justify-center shrink-0 text-accent">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </div>
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="font-semibold text-[15px] text-text-primary">Biometric Verification</span>
              <span className="text-[11px] font-semibold text-accent uppercase tracking-wide">Recommended</span>
              <span className="text-[13px] text-text-secondary leading-snug">Quick face scan confirms you're 18+ and a real person. Unlocks full access.</span>
            </div>
            <span className="text-[13px] font-semibold text-accent whitespace-nowrap shrink-0">{loading ? 'Starting...' : 'Verify Now'}</span>
          </Button>

          <Button type="button" variant="ghost" onClick={() => setShowDobForm(true)} disabled={loading}
            className="flex items-center gap-3.5 w-full p-4 h-auto rounded-xl text-left transition-all border border-border-light bg-bg-secondary hover:bg-transparent active:bg-transparent hover:border-accent disabled:opacity-60 disabled:cursor-not-allowed">
            <div className="w-10 h-10 rounded-full bg-bg-tertiary flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            </div>
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="font-semibold text-[15px] text-text-primary">Date of Birth</span>
              <span className="text-[13px] text-text-secondary leading-snug">Self-attestation -- limited access until biometric verification</span>
            </div>
            <span className="text-[13px] font-semibold text-text-secondary whitespace-nowrap shrink-0">Use Date of Birth</span>
          </Button>

          <p className="text-xs text-text-tertiary text-center mt-1">You can upgrade your verification level anytime in Account Settings.</p>
          {error && <div className="bg-danger/10 border border-danger/20 text-danger py-3 px-4 rounded-lg text-sm text-center dark:bg-[var(--theme-danger-callout-bg)] dark:border-[var(--theme-danger-callout-border)] dark:text-[var(--theme-danger-callout-text)]" role="alert">{error}</div>}
        </div>
      ) : (
        <form onSubmit={handleDobSubmit} className="flex flex-col gap-5">
          {showDobForm && diditAvailable && (
            <Button type="button" variant="text" size="sm" onClick={() => { setShowDobForm(false); setError(''); }}
              className="p-0 mb-2 text-[13px] justify-start">
              {'\u2190'} Back to verification options
            </Button>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="dateOfBirth" className="font-medium text-text-primary text-sm">Date of Birth</label>
            <input type="date" id="dateOfBirth" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)}
              max={formatDateOnlyFromDate(minAge18Date)} min={formatDateOnlyFromDate(maxAge120Date)} required
              className="py-3 px-4 border border-border-medium rounded-lg text-base bg-bg-primary text-text-primary focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/10" />
            <span className="text-xs text-text-tertiary">You must be 18 or older to use this service</span>
          </div>
          {error && <div className="bg-danger/10 border border-danger/20 text-danger py-3 px-4 rounded-lg text-sm text-center dark:bg-[var(--theme-danger-callout-bg)] dark:border-[var(--theme-danger-callout-border)] dark:text-[var(--theme-danger-callout-text)]" role="alert">{error}</div>}
          <div className="bg-bg-tertiary rounded-lg p-4">
            <p className="m-0 text-[13px] text-text-secondary leading-relaxed text-center">You must be 18 years or older to use this service. By continuing, you confirm you meet this requirement.</p>
          </div>
          <Button variant="primary" type="submit" disabled={loading || !dateOfBirth} fullWidth>
            {loading ? 'Verifying...' : 'Verify Age'}
          </Button>
        </form>
      )}
    </Modal>
  );
};
